import React, { useMemo } from 'react';
import { Plus, Minus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTraceSidebar } from '@/contexts/TraceSidebarContext';
import { WaterfallRow } from './WaterfallRow';
import { WaterfallTimeline } from './WaterfallTimeline';
import type { TraceItem, WaterfallTimings } from '@/types/trace';
import { flattenTraceItems, calculateWaterfallTimings } from '@/utils/traceTransformer';

interface WaterfallViewProps {
  items: TraceItem[];
}

export function WaterfallView({ items }: WaterfallViewProps) {
  const { state, zoomIn, zoomOut, resetZoom } = useTraceSidebar();

  const flatItems = useMemo(() => flattenTraceItems(items), [items]);
  const timings = useMemo(() => calculateWaterfallTimings(items), [items]);

  if (flatItems.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        No trace items found
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Zoom Controls */}
      <div className="flex items-center justify-end gap-1 p-2 border-b">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={zoomOut}
          disabled={state.waterfallZoom <= 0.2}
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
        <span className="text-xs text-muted-foreground w-12 text-center">
          {Math.round(state.waterfallZoom * 100)}%
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={zoomIn}
          disabled={state.waterfallZoom >= 5}
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={resetZoom}
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Timeline Header */}
      <WaterfallTimeline timings={timings} zoom={state.waterfallZoom} />

      {/* Rows */}
      <ScrollArea className="flex-1">
        <div className="min-w-max">
          {flatItems.map((item) => (
            <WaterfallRow
              key={item.id}
              item={item}
              timings={timings}
              zoom={state.waterfallZoom}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export default WaterfallView;
